import { useSearchParams } from 'react-router-dom';

import React, { createContext, useContext, useMemo } from 'react';

interface SkeletonDebugContextValue {
  forceSkeleton: boolean;
  debugDelay: number;
}

const SkeletonDebugContext = createContext<SkeletonDebugContextValue>({
  forceSkeleton: false,
  debugDelay: 0,
});

/**
 * Hook to check if skeleton debug mode is enabled
 * Usage: const { forceSkeleton } = useSkeletonDebug();
 *
 * URL Parameters:
 * - ?skeleton=true - Forces skeleton display
 * - ?skeleton=1000 - Forces skeleton with 1000ms delay
 * - ?skeleton=false - Disables skeleton debug (normal behavior)
 */
export function useSkeletonDebug() {
  const context = useContext(SkeletonDebugContext);
  if (!context) {
    // Fallback for when used outside provider
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [searchParams] = useSearchParams();
    const skeletonParam = searchParams.get('skeleton');

    return {
      forceSkeleton:
        skeletonParam === 'true' ||
        (skeletonParam !== null && !isNaN(Number(skeletonParam))),
      debugDelay: !isNaN(Number(skeletonParam)) ? Number(skeletonParam) : 0,
    };
  }
  return context;
}

interface SkeletonDebugProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that enables global skeleton debug mode
 * Add this to your app layout or providers
 */
export function SkeletonDebugProvider({
  children,
}: SkeletonDebugProviderProps) {
  const [searchParams] = useSearchParams();

  const value = useMemo(() => {
    const skeletonParam = searchParams.get('skeleton');

    // Parse the skeleton parameter
    // Can be: true, false, or a number (delay in ms)
    let forceSkeleton = false;
    let debugDelay = 0;

    if (skeletonParam === 'true') {
      forceSkeleton = true;
    } else if (skeletonParam && !isNaN(Number(skeletonParam))) {
      forceSkeleton = true;
      debugDelay = Number(skeletonParam);
    }

    return { forceSkeleton, debugDelay };
  }, [searchParams]);

  return (
    <SkeletonDebugContext.Provider value={value}>
      {children}
    </SkeletonDebugContext.Provider>
  );
}

/**
 * Higher-order component to wrap any component with skeleton debug support
 * Usage: export default withSkeletonDebug(YourComponent);
 */
export function withSkeletonDebug<P extends object>(
  Component: React.ComponentType<P>,
  SkeletonComponent: React.ComponentType
) {
  return function WithSkeletonDebugComponent(props: P) {
    const { forceSkeleton, debugDelay } = useSkeletonDebug();
    const [showSkeleton, setShowSkeleton] = React.useState(forceSkeleton);

    React.useEffect(() => {
      if (forceSkeleton && debugDelay > 0) {
        setShowSkeleton(true);
        const timer = setTimeout(() => {
          setShowSkeleton(false);
        }, debugDelay);
        return () => clearTimeout(timer);
      } else {
        setShowSkeleton(forceSkeleton);
      }
    }, [forceSkeleton, debugDelay]);

    if (showSkeleton) {
      return <SkeletonComponent />;
    }

    return <Component {...props} />;
  };
}
